/**
 * Setup Service
 *
 * Provides API functions for the first-launch setup wizard.
 * Handles system checks, component detection, and wizard state.
 */

import { api } from './api-adapters';

/**
 * Unwrap the backend {success, data} envelope.
 * Backend setup endpoints always return {success: boolean, data: T}.
 * Throws if the envelope is missing or success is false.
 */
function unwrap<T>(responseData: unknown): T {
  if (responseData === null || responseData === undefined || typeof responseData !== 'object') {
    throw new Error(
      `[setupService] Expected {success, data} envelope but received: ${JSON.stringify(responseData)}`
    );
  }
  const obj = responseData as Record<string, unknown>;
  if (!('success' in obj) || !('data' in obj)) {
    throw new Error(
      `[setupService] Response missing required envelope fields (success, data): ${JSON.stringify(obj)}`
    );
  }
  if (obj.success !== true) {
    const errMsg =
      typeof obj.error === 'object' && obj.error !== null
        ? String((obj.error as Record<string, unknown>).message ?? JSON.stringify(obj.error))
        : String(obj.error ?? 'Request failed');
    throw new Error(`[setupService] API returned success=false: ${errMsg}`);
  }
  return obj.data as T;
}

// Types

export interface GPUCheckResult {
  name: string;
  vendor: string;
  vramGb: number;
  backend: string;
  driverVersion: string;
  isAvailable: boolean;
  isSupportedForAi: boolean;
  recommendation: string;
}

export interface RAMCheckResult {
  totalGb: number;
  availableGb: number;
  isSufficient: boolean;
  recommendation: string;
}

export interface DiskCheckResult {
  path: string;
  diskType: string;
  totalGb: number;
  freeGb: number;
  writeSpeedMbps: number;
  isSufficient: boolean;
  recommendation: string;
}

export interface OSCheckResult {
  name: string;
  version: string;
  architecture: string;
  isCompatible: boolean;
  recommendation: string;
}

export interface SystemCheckResult {
  gpu: GPUCheckResult;
  ram: RAMCheckResult;
  disk: DiskCheckResult;
  os: OSCheckResult;
  overallStatus: 'excellent' | 'good' | 'limited' | 'insufficient';
  canRunAllLocally: boolean;
  recommendedComponents: string[];
  /**
   * True when the GPU overlay benchmark cache is present on disk after the
   * system check (or when no NVIDIA GPU is detected, so the benchmark is
   * not applicable). False means the install lacks a calibrated overlay
   * limit and the wizard MUST keep the user on this step until they retry
   * — see RENDER_PASS_PLANNER.MAX_GPU_OVERLAY_COUNT for why this matters
   * (uncalibrated installs hit the chunk-split zoom regression).
   */
  gpuBenchmarkReady: boolean;
}

export interface SetupComponentInfo {
  id: string;
  name: string;
  description: string;
  icon: string;
  category: string;
  sizeMb: number;
  requiresGpu: boolean;
  minVramGb: number;
  minDiskGb: number;
  cloudProvider: string | null;
  docsUrl: string;
  features: string[];
}

export interface DetectedInstallation {
  path: string;
  version: string | null;
  hasModels: boolean;
  isValid: boolean;
  details: Record<string, unknown>;
}

export interface ComponentDetectionResult {
  componentId: string;
  isDetected: boolean;
  isRunning: boolean;
  detectedInstallations: DetectedInstallation[];
  recommendedInstallation: DetectedInstallation | null;
  canRunLocally: boolean;
  meetsRequirements: boolean;
  missingRequirements: string[];
  cloudAvailable: boolean;
  error: string | null;
  // TTS providers specific data (when componentId === 'tts_providers')
  providers?: TTSProviderInfo[];
}

export interface TTSProviderInfo {
  id: string;
  name: string;
  description: string;
  providerType: 'local_gpu' | 'local_cpu' | 'cloud_free' | 'cloud_paid';
  isAvailable: boolean;
  isInstalled: boolean;
  languageCount: number;
  languages: string[];
  features: string[];
  requiresGpu: boolean;
  requiresApiKey: boolean;
  sizeMb: number;
  error: string | null;
  // New fields for better UX
  unavailableReason: string | null; // Human-readable reason why not available
  cloudFallbackAvailable: boolean; // Can use via RunPod cloud
  supportedPlatforms: string[]; // ['windows', 'linux', 'darwin']
  minVramGb: number; // Minimum VRAM required for GPU providers
}

export interface ComponentConfiguration {
  componentId: string;
  option: 'local' | 'cloud' | 'skip' | 'use_existing';
  installationPath: string | null;
  configuredAt: string | null;
}

export interface WizardState {
  isCompleted: boolean;
  currentStep: string;
  completedSteps: string[];
  componentConfigurations: Record<string, ComponentConfiguration>;
  startedAt: string | null;
  completedAt: string | null;
  version: string;
  selectedUseCases?: string[];
}

export interface WizardStepInfo {
  id: string;
  name: string;
  description: string;
  icon: string;
  isComponentStep: boolean;
  componentId: string | null;
  canSkip: boolean;
}

export interface WizardStepsResponse {
  steps: WizardStepInfo[];
  totalSteps: number;
}

// API Functions

/**
 * Run a comprehensive system check
 */
export async function getSystemCheck(): Promise<SystemCheckResult> {
  const response = await api.get<SystemCheckResult>('/api/v1/setup/system-check');
  return unwrap<SystemCheckResult>(response.data);
}

/**
 * Get all setup components
 */
export async function getSetupComponents(): Promise<Record<string, SetupComponentInfo>> {
  const response = await api.get<Record<string, SetupComponentInfo>>('/api/v1/setup/components');
  return unwrap<Record<string, SetupComponentInfo>>(response.data);
}

/**
 * Detect a specific component
 */
export async function detectComponent(componentId: string): Promise<ComponentDetectionResult> {
  const response = await api.get<ComponentDetectionResult>(
    `/api/v1/setup/component/${componentId}`
  );
  return unwrap<ComponentDetectionResult>(response.data);
}

/**
 * Configure a component
 */
export async function configureComponent(
  componentId: string,
  option: 'local' | 'cloud' | 'skip' | 'use_existing',
  installationPath?: string
): Promise<{ success: boolean; componentId: string; option: string; installationPath?: string }> {
  const response = await api.post<{
    success: boolean;
    componentId: string;
    option: string;
    installationPath?: string;
  }>(`/api/v1/setup/component/${componentId}/configure`, {
    option,
    installationPath,
  });
  return response.data;
}

/**
 * Get current wizard state
 */
export async function getWizardState(): Promise<WizardState> {
  const response = await api.get<WizardState>('/api/v1/setup/wizard-state');
  return unwrap<WizardState>(response.data);
}

/**
 * Update wizard state
 */
export async function updateWizardState(
  currentStep: string,
  completed?: boolean,
  selectedUseCases?: string[]
): Promise<WizardState> {
  const response = await api.put<WizardState>('/api/v1/setup/wizard-state', {
    currentStep,
    completed,
    ...(selectedUseCases !== undefined ? { selectedUseCases } : {}),
  });
  return unwrap<WizardState>(response.data);
}

/**
 * Mark wizard as completed
 */
export async function completeWizard(): Promise<WizardState> {
  const response = await api.post<WizardState>('/api/v1/setup/wizard-complete');
  return unwrap<WizardState>(response.data);
}

/**
 * Reset wizard state
 */
export async function resetWizard(): Promise<WizardState> {
  const response = await api.post<WizardState>('/api/v1/setup/wizard-reset');
  return unwrap<WizardState>(response.data);
}

/**
 * Get wizard steps configuration
 */
export async function getWizardSteps(): Promise<WizardStepsResponse> {
  const response = await api.get<WizardStepsResponse>('/api/v1/setup/wizard-steps');
  return unwrap<WizardStepsResponse>(response.data);
}

/**
 * Check if this is the first launch
 */
export async function checkIsFirstLaunch(): Promise<boolean> {
  const response = await api.get<{ isFirstLaunch: boolean }>('/api/v1/setup/is-first-launch');
  const data = unwrap<{ isFirstLaunch: boolean }>(response.data);
  return data.isFirstLaunch;
}

// ---- New types for use-case-driven wizard ----

export interface UseCaseDefinition {
  id: string;
  name: string;
  description: string;
  icon: string;
  components: string[];
  optionalComponents: string[];
  diskEstimateGb: number;
}

export interface ResolvedComponent {
  id: string;
  name: string;
  description: string;
  diskGb: number;
  requiresGpu: boolean;
  cloudAvailable: boolean;
  alreadyInstalled: boolean;
}

export interface ResolvedDependencies {
  components: ResolvedComponent[];
  optionalComponents: ResolvedComponent[];
  totalDiskGb: number;
  availableDiskGb: number;
  sufficient: boolean;
  perComponent: Record<string, number>;
}

// ---- New API functions ----

/**
 * Get all use case definitions
 */
export async function getUseCases(): Promise<UseCaseDefinition[]> {
  const response = await api.get<UseCaseDefinition[]>('/api/v1/setup/use-cases');
  const data = unwrap<{ useCases: UseCaseDefinition[] }>(response.data);
  return data.useCases ?? (response.data as unknown as UseCaseDefinition[]);
}

/**
 * Resolve dependencies for selected use cases
 */
export async function resolveUseCaseDependencies(
  useCases: string[]
): Promise<ResolvedDependencies> {
  const response = await api.post<ResolvedDependencies>('/api/v1/setup/resolve-dependencies', {
    useCases,
  });
  const raw = unwrap<{
    components: string[];
    optionalComponents?: string[];
    totalDiskGb: number;
    availableDiskGb: number;
    sufficient: boolean;
    perComponent: Record<
      string,
      {
        name: string;
        description?: string;
        diskGb: number;
        requiresGpu: boolean;
        cloudAvailable: boolean;
        alreadyInstalled: boolean;
      }
    >;
  }>(response.data);

  // requiresGpu comes straight from the backend's perComponent payload
  // (SETUP_COMPONENTS.requires_gpu / COMPONENT_REGISTRY requirements
  // ["gpu_required"], see api_server/local_routes/setup.py::_resolve_info)
  // instead of a hardcoded id list here that would drift the moment a new
  // GPU-only component (e.g. a new local SFX model) is added.
  const toResolvedComponent = (compId: string): ResolvedComponent => {
    const info = raw.perComponent[compId];
    return {
      id: compId,
      name: info?.name ?? compId,
      description: info?.description ?? '',
      diskGb: info?.diskGb ?? 0,
      requiresGpu: info?.requiresGpu ?? false,
      cloudAvailable: info?.cloudAvailable ?? false,
      alreadyInstalled: info?.alreadyInstalled ?? false,
    };
  };

  const components: ResolvedComponent[] = raw.components.map(toResolvedComponent);
  const optionalComponents: ResolvedComponent[] = (raw.optionalComponents ?? []).map(
    toResolvedComponent
  );

  return {
    components,
    optionalComponents,
    totalDiskGb: raw.totalDiskGb,
    availableDiskGb: raw.availableDiskGb,
    sufficient: raw.sufficient,
    perComponent: Object.fromEntries(
      Object.entries(raw.perComponent).map(([k, v]) => [k, v.diskGb])
    ),
  };
}

/**
 * Start batch installation of components
 */
export async function startBatchInstall(componentIds: string[]): Promise<{ status: string }> {
  const response = await api.post<{ status: string }>('/api/v1/setup/install-batch', {
    componentIds,
  });
  return unwrap<{ status: string }>(response.data);
}

/**
 * Cancel batch installation
 */
export async function cancelBatchInstall(): Promise<{ status: string }> {
  const response = await api.post<{ status: string }>('/api/v1/setup/install-batch/cancel');
  return response.data;
}

/**
 * Set a custom install path for a component via the components API
 */
export async function setComponentInstallPath(
  componentId: string,
  path: string | null
): Promise<{ path: string | null; message: string }> {
  const response = await api.post<{ path: string | null; message: string }>(
    `/api/v1/components/${componentId}/path`,
    { path }
  );
  return response.data;
}

/**
 * Get the current install path for a component
 */
export async function getComponentInstallPath(
  componentId: string
): Promise<{ customPath: string | null; effectivePath: string }> {
  const response = await api.get<{ customPath: string | null; effectivePath: string }>(
    `/api/v1/components/${componentId}/path`
  );
  return response.data;
}

// Helper functions

/**
 * Get status color class based on overall status
 */
export function getStatusColor(status: SystemCheckResult['overallStatus']): string {
  switch (status) {
    case 'excellent':
      return 'text-green-600 dark:text-green-400';
    case 'good':
      return 'text-blue-600 dark:text-blue-400';
    case 'limited':
      return 'text-yellow-600 dark:text-yellow-400';
    case 'insufficient':
      return 'text-red-600 dark:text-red-400';
    default:
      return 'text-secondary-600 dark:text-secondary-400';
  }
}

/**
 * Get status badge for system check
 */
export function getStatusBadge(status: SystemCheckResult['overallStatus']): {
  text: string;
  variant: string;
} {
  switch (status) {
    case 'excellent':
      return { text: 'Excellent', variant: 'success' };
    case 'good':
      return { text: 'Good', variant: 'info' };
    case 'limited':
      return { text: 'Limited', variant: 'warning' };
    case 'insufficient':
      return { text: 'Insufficient', variant: 'error' };
    default:
      return { text: 'Unknown', variant: 'default' };
  }
}

/**
 * Format size in GB/MB
 */
export function formatSize(sizeMb: number): string {
  if (sizeMb >= 1000) {
    return `${(sizeMb / 1000).toFixed(1)} GB`;
  }
  return `${sizeMb} MB`;
}

/**
 * Get component icon name
 */
export function getComponentIcon(componentId: string): string {
  const icons: Record<string, string> = {
    fooocus: 'image',
    whisperx: 'mic',
    nllb: 'languages',
    ffmpeg: 'video',
    kokoro_tts: 'volume-2',
    piper_tts: 'volume-1',
    tts_providers: 'volume-2',
  };
  return icons[componentId] || 'package';
}

/**
 * Get provider type display info
 */
export function getProviderTypeInfo(providerType: TTSProviderInfo['providerType']): {
  label: string;
  badge: string;
  color: string;
} {
  switch (providerType) {
    case 'local_gpu':
      return { label: 'Local (GPU)', badge: 'GPU', color: 'text-purple-600 dark:text-purple-400' };
    case 'local_cpu':
      return { label: 'Local (CPU)', badge: 'CPU', color: 'text-blue-600 dark:text-blue-400' };
    case 'cloud_free':
      return { label: 'Cloud (Free)', badge: 'Free', color: 'text-green-600 dark:text-green-400' };
    case 'cloud_paid':
      return {
        label: 'Cloud (Paid)',
        badge: 'Paid',
        color: 'text-yellow-600 dark:text-yellow-400',
      };
    default:
      return { label: 'Unknown', badge: '?', color: 'text-secondary-600 dark:text-secondary-400' };
  }
}
